import React, { useMemo } from 'react';
import type { PlaybookListItem } from '../shared/playbook-types';
import '../styles/PlaybookCommon.css';

interface PlaybookSidebarProps {
    title: string;
    items: PlaybookListItem[];
    selectedId: number | null;
    onSelectItem: (id: number) => void;
    isLoading: boolean;
    searchQuery: string;
    onSearchChange: (query: string) => void;
    searchPlaceholder?: string;
    emptyMessage?: string;
    onAdd?: () => void;
    onEditItem?: (id: number, name: string) => void;
    onDeleteItem?: (id: number, name: string) => void;
}

export const PlaybookSidebar: React.FC<PlaybookSidebarProps> = ({
    title,
    items,
    selectedId,
    onSelectItem,
    isLoading,
    searchQuery,
    onSearchChange,
    searchPlaceholder = 'Rechercher...',
    emptyMessage = 'Aucun élément trouvé',
    onAdd,
    onEditItem,
    onDeleteItem
}) => {
    // Filter items based on search query
    const filteredItems = useMemo(() => {
        if (!searchQuery.trim()) {
            return items;
        }
        const query = searchQuery.toLowerCase();
        return items.filter(item =>
            item.name.toLowerCase().includes(query)
        );
    }, [items, searchQuery]);

    return (
        <div className="playbook-sidebar">
            <div className="playbook-sidebar-header">
                <h2>{title}</h2>
                {onAdd && (
                    <button
                        type="button"
                        className="playbook-add-btn"
                        onClick={onAdd}
                        title="Ajouter"
                        aria-label="Ajouter"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="12" y1="5" x2="12" y2="19"></line>
                            <line x1="5" y1="12" x2="19" y2="12"></line>
                        </svg>
                        <span>Ajouter</span>
                    </button>
                )}
            </div>
            {isLoading ? (
                <div className="playbook-list-loading">
                    <div className="loading-spinner"></div>
                </div>
            ) : (
                <div className="playbook-list">
                    <div className="playbook-list-header">
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={(e) => onSearchChange(e.target.value)}
                            placeholder={searchPlaceholder}
                            className="playbook-search-input"
                        />
                        <svg className="search-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <circle cx="11" cy="11" r="8"></circle>
                            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                        </svg>
                    </div>
                    <div className="playbook-list-items">
                        {filteredItems.map((item) => (
                            <div
                                key={item.id}
                                className={`playbook-list-item ${selectedId === item.id ? 'active' : ''}`}
                                onClick={() => onSelectItem(item.id)}
                            >
                                <span className="playbook-item-name">{item.name}</span>
                                <div className="playbook-item-actions">
                                    {onEditItem && (
                                        <button
                                            type="button"
                                            className="playbook-item-edit-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onEditItem(item.id, item.name);
                                            }}
                                            title="Modifier"
                                            aria-label={`Modifier ${item.name}`}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z"></path>
                                            </svg>
                                        </button>
                                    )}
                                    {onDeleteItem && (
                                        <button
                                            type="button"
                                            className="playbook-item-delete-btn"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                onDeleteItem(item.id, item.name);
                                            }}
                                            title="Supprimer"
                                            aria-label={`Supprimer ${item.name}`}
                                        >
                                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                                <path d="M3 6h18"></path>
                                                <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
                                                <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
                                            </svg>
                                        </button>
                                    )}
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="chevron-icon">
                                        <polyline points="9 18 15 12 9 6"></polyline>
                                    </svg>
                                </div>
                            </div>
                        ))}
                        {filteredItems.length === 0 && items.length > 0 && (
                            <div className="playbook-list-empty">
                                Aucun résultat pour "{searchQuery}"
                            </div>
                        )}
                        {items.length === 0 && (
                            <div className="playbook-list-empty">
                                {emptyMessage}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};
